import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Target, 
  TrendingUp, 
  Award, 
  CheckCircle, 
  ArrowRight,
  Building2,
  Briefcase,
  Globe,
  Zap
} from 'lucide-react';
import Hero from '../components/Hero';
import AboutSection from '../components/AboutSection';
import ServicesPreview from '../components/ServicesPreview';
import StatsSection from '../components/StatsSection';
import ContactSection from '../components/ContactSection';
import PartnersSection from '../components/PartnersSection';
import './HomePage.css';

const HomePage: React.FC = () => {
  const features = [
    {
      icon: Target,
      title: 'Precision Matching',
      description: 'We filter the right talent for the right job, ensuring perfect alignment between candidates and opportunities.'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Our experienced recruiters understand industry nuances and deliver exceptional results.'
    },
    {
      icon: TrendingUp,
      title: 'Proven Success',
      description: 'Established in 2013, we have a track record of connecting talent with leading organizations.'
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'We scout for talent that has the ability to lead, sustain, and thrive in their roles.'
    }
  ];

  const whyChooseUs = [
    'Tech-based integrated solutions',
    'Comprehensive talent scouting',
    'Industry expertise across sectors',
    'Personalized recruitment approach',
    'Long-term partnership focus',
    'Proven track record since 2013'
  ];

  return (
    <div className="home-page">
      <Hero />
      
      <StatsSection />
      
      <div id="about">
        <AboutSection />
      </div>
      
      <div id="services">
        <ServicesPreview />
      </div>
      
      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why Choose Future Tech HR?</h2>
            <p className="section-subtitle">
              We don't just fill positions - we build successful careers and thriving organizations.
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  <feature.icon size={32} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section section">
        <div className="container">
          <div className="why-choose-content">
            <motion.div
              className="why-choose-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>What Makes Us Unique?</h2>
              <p>
                Our ability to filter the right talent for the right job and bring out the best 
                human capabilities the task requires sets us apart. When you have the right person 
                at the right place and the right time, magic follows!
              </p>
              <ul className="why-choose-list">
                {whyChooseUs.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <CheckCircle size={20} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="why-choose-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="visual-cards">
                <motion.div
                  className="visual-card"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <Building2 size={24} />
                  <span>Corporate Excellence</span>
                </motion.div>
                <motion.div
                  className="visual-card"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <Briefcase size={24} />
                  <span>Career Growth</span>
                </motion.div>
                <motion.div
                  className="visual-card"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Globe size={24} />
                  <span>Global Reach</span>
                </motion.div>
                <motion.div
                  className="visual-card"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  viewport={{ once: true }}
                >
                  <Zap size={24} />
                  <span>Fast Results</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <PartnersSection />
      
      <ContactSection />
    </div>
  );
};

export default HomePage;
